/**
 * Web stand-in for `react-native-share`.
 *
 * Uses the Web Share API when the browser offers it (Edge/Chrome on Windows
 * do for https origins), and otherwise falls back to a plain download, which
 * is what "share a file" means on a desktop POS terminal anyway.
 */

export type ShareOpenOptions = {
  url?: string;
  urls?: string[];
  title?: string;
  message?: string;
  filename?: string;
  saveToFiles?: boolean;
  failOnCancel?: boolean;
};

export type ShareResponse = {success: boolean; message: string};

async function download(url: string, filename?: string): Promise<void> {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename || '';
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

const Share = {
  async open(options: ShareOpenOptions): Promise<ShareResponse> {
    const url = options.url || (options.urls && options.urls[0]) || '';
    const nav = typeof navigator !== 'undefined' ? (navigator as any) : undefined;

    if (nav && typeof nav.share === 'function' && !options.saveToFiles) {
      try {
        await nav.share({title: options.title, text: options.message, url: url || undefined});
        return {success: true, message: 'shared'};
      } catch (error: any) {
        if (error && error.name === 'AbortError') {
          return {success: false, message: 'dismissed'};
        }
        // fall through to the download path
      }
    }

    if (url) {
      await download(url, options.filename);
      return {success: true, message: 'downloaded'};
    }
    return {success: false, message: 'nothing to share'};
  },

  async shareSingle(options: ShareOpenOptions): Promise<ShareResponse> {
    return Share.open(options);
  },

  Social: {},
};

export default Share;
